import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getAutocompleteFieldsByCountry,
  resetAutocompleteFields,
  getSelectedTask,
} from '@domains/documents-review/store/slices/document-review-slice';
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { CountryCode } from '@domains/country';
import { AutocompleteCard } from './components/AutocompleteCard';
import { AutocompleteAnalytics } from '@domains/documents-review/components/AutocompleteAnalytics/AutocompleteAnalytics';
import { SuggestionsAutoCompleteFieldItem } from '@domains/documents-review/store/api-slices/suggestions-autocomplete-docs.endpoints';
import { useSuggestionsAutoCompleteDocs } from '@domains/documents-review/hooks/useSuggestionsAutoCompleteDocs';
import { AutocompleteFieldItem } from '@domains/documents-review/interfaces/interfaces';
import { WrapperAutocomplete } from './styles';

const mergeServiceWithFeedback = (
  serviceItems: SuggestionsAutoCompleteFieldItem[],
  feedbackItems: AutocompleteFieldItem[]
): AutocompleteFieldItem[] => {
  const feedbackMap = new Map((feedbackItems || []).map(i => [i.field, i]));
  return serviceItems.map(svc => {
    const fb = feedbackMap.get(svc.field);
    const merged = {
      field: svc.field,
      fieldId: svc.fieldId,
      fieldConfidenceLevel: svc.fieldConfidenceLevel,
      suggestions: svc.suggestions,
      defaultValue: svc.defaultValue,
      defaultModel: svc.defaultModel,
      defaultSource: svc.defaultSource,
      defaultSuggestionId: svc.defaultSuggestionId,
      finalValue: fb?.finalValue ?? '',
      userFeedback: typeof fb?.userFeedback === 'number' ? fb.userFeedback : 0,
      selectedModel: fb?.selectedModel ?? '',
      selectedSuggestionId: fb?.selectedSuggestionId ?? null,
      finalConfidence: fb?.finalConfidence,
      agentComment: fb?.agentComment,
      reviewed: fb?.reviewed ?? false,
    } as AutocompleteFieldItem;

    return merged;
  });
};

export const AutocompleteFieldsList = () => {
  const dispatch = useDispatch();
  const byCountry = useSelector(getAutocompleteFieldsByCountry);
  const country = useSelector(getCountry) as CountryCode;
  const selectedTask = useSelector(getSelectedTask);
  const nid = selectedTask?.nid;

  const { data: svcItems } = useSuggestionsAutoCompleteDocs();

  useEffect(() => {
    if (!byCountry) {
      dispatch(resetAutocompleteFields());
    }
  }, [byCountry, dispatch]);

  const items = useMemo((): AutocompleteFieldItem[] => {
    const feedbackItems =
      country && byCountry && byCountry[country] ? byCountry[country] : [];

    const canUseService = Boolean(
      country && nid && Array.isArray(svcItems) && svcItems.length > 0
    );

    return canUseService
      ? mergeServiceWithFeedback(
          svcItems as SuggestionsAutoCompleteFieldItem[],
          feedbackItems
        )
      : feedbackItems || [];
  }, [country, nid, byCountry, svcItems]);

  const analyticsProps = useMemo(() => {
    const expected =
      byCountry && country && byCountry[country] ? byCountry[country] : [];
    const totalExpectedFields = expected.length || items.length;
    let nonFillableFieldsCount = 0;
    if (svcItems && svcItems.length && expected.length) {
      const svcMap = new Map(svcItems.map(s => [s.field, s]));
      nonFillableFieldsCount = expected.filter(f => {
        const svc = svcMap.get(f.field);
        const hasSuggestions = (svc?.suggestions?.length || 0) > 0;
        return !svc || !hasSuggestions;
      }).length;
    }
    return { totalExpectedFields, nonFillableFieldsCount };
  }, [byCountry, country, items.length, svcItems]);

  if (!country) {
    return (
      <div
        data-testid="autocomplete-fields-list"
        className="d-flex flex-column gap-3"
      >
        <div className="text-muted">
          Selecciona un país para ver las sugerencias
        </div>
      </div>
    );
  }

  return (
    <WrapperAutocomplete data-testid="autocomplete-fields-list">
      <div className="p-1">
        <AutocompleteAnalytics
          items={items}
          totalExpectedFields={analyticsProps.totalExpectedFields}
          nonFillableFieldsCount={analyticsProps.nonFillableFieldsCount}
        />
        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
          {items.map((item, index) => (
            <div
              className="col"
              key={item?.field || `autocomplete-item-${index}`}
            >
              <AutocompleteCard item={item} />
            </div>
          ))}
        </div>
      </div>
    </WrapperAutocomplete>
  );
};
